/**
 * domains/ai/thesis2/gates.ts — MER Algo Footprints v7.1H
 *
 * Deterministic gating layer.
 * AI cannot mark a thesis ACTIONABLE unless all hard gates pass.
 * If gates fail, thesis state AND posture are both downgraded deterministically.
 *
 * This file contains ONLY deterministic logic — no AI calls.
 */

import {
  ThesisEvidenceInput, GateConfig, GateResult, DEFAULT_GATE_CONFIG,
  ConfidenceBreakdown, ThesisState, ActionPosture,
} from './types';
import { TrapSeverity } from '../../../shared/types';

// ─── Gate evaluation ──────────────────────────────────────────────────────────

export interface GateEvaluation {
  passed:        boolean;
  results:       GateResult[];
  failures:      string[];
  downgraded_to: ThesisState | null;   // null = gates passed
  forced_posture: ActionPosture | null; // non-null when posture must be overridden
}

export const SEVERITY_RANK: Record<TrapSeverity, number> = {
  None: 0, Low: 1, Medium: 2, High: 3,
};

export function evaluateDeterministicGates(
  input: ThesisEvidenceInput,
  cfg: GateConfig = DEFAULT_GATE_CONFIG,
): GateEvaluation {
  const results:  GateResult[] = [];
  const failures: string[]     = [];

  // ── Gate 1: Setup score ──────────────────────────────────────────────────
  const topSetup   = input.setups[0] ?? null;
  const setupScore = topSetup?.confidence ?? 0;
  const g1 = setupScore >= cfg.min_setup_score;
  results.push({
    gate: 'setup_score', passed: g1,
    value: setupScore, required: cfg.min_setup_score,
    note: g1 ? null : `Setup score ${setupScore} < required ${cfg.min_setup_score}`,
  });
  if (!g1) failures.push(`Insufficient setup score (${setupScore})`);

  // ── Gate 2: Risk/reward ──────────────────────────────────────────────────
  const rr = input.risk?.expectedR ?? null;
  const g2 = rr !== null && rr >= cfg.min_risk_reward;
  results.push({
    gate: 'risk_reward', passed: g2,
    value: rr, required: cfg.min_risk_reward,
    note: g2 ? null
      : rr === null ? 'R/R not calculable (incomplete levels)'
      : `R/R ${rr.toFixed(2)} < required ${cfg.min_risk_reward}`,
  });
  if (!g2) failures.push(`R/R insufficient (${rr?.toFixed(2) ?? 'N/A'})`);

  // ── Gate 3: Regime quality ───────────────────────────────────────────────
  const regimeScore = input.regime?.confidence ?? 0;
  const g3 = regimeScore >= cfg.min_regime_quality;
  results.push({
    gate: 'regime_quality', passed: g3,
    value: regimeScore, required: cfg.min_regime_quality,
    note: g3 ? null : `Regime confidence ${regimeScore} < required ${cfg.min_regime_quality}`,
  });
  if (!g3) failures.push(`Regime quality low (${regimeScore})`);

  // ── Gate 4: Trap severity ────────────────────────────────────────────────
  const highestTrapSeverity = input.traps.reduce<TrapSeverity>((worst, t) =>
    SEVERITY_RANK[t.severity] > SEVERITY_RANK[worst] ? t.severity : worst, 'None');
  const maxAllowed = cfg.max_trap_severity;
  const g4 = SEVERITY_RANK[highestTrapSeverity] <= SEVERITY_RANK[maxAllowed];
  results.push({
    gate: 'trap_severity', passed: g4,
    value: highestTrapSeverity, required: `<= ${maxAllowed}`,
    note: g4 ? null : `Trap severity ${highestTrapSeverity} exceeds max allowed ${maxAllowed}`,
  });
  if (!g4) failures.push(`High trap risk (${highestTrapSeverity})`);

  // ── Gate 5: Structure integrity ──────────────────────────────────────────
  const smc          = input.smc;
  const hasStructure = !cfg.require_structure || !!(smc?.lastBOS || smc?.lastCHOCH);
  results.push({
    gate: 'structure_event', passed: hasStructure,
    value: hasStructure, required: cfg.require_structure,
    note: hasStructure ? null : 'No BOS or CHOCH detected — structure not confirmed',
  });
  if (!hasStructure) failures.push('No structural confirmation (BOS/CHOCH missing)');

  // ── Gate 6: Hard conflict flags ──────────────────────────────────────────
  const hardConflict = !!(input.risk?.trapConflict && input.risk?.regimeConflict);
  results.push({
    gate: 'no_hard_conflict', passed: !hardConflict,
    value: hardConflict, required: false,
    note: hardConflict ? 'Both trap conflict AND regime conflict active simultaneously' : null,
  });
  if (hardConflict) failures.push('Hard conflict: trap + regime both opposing');

  const allPassed = failures.length === 0;

  // ── Downgrade state AND posture together ─────────────────────────────────
  let downgraded_to:  ThesisState   | null = null;
  let forced_posture: ActionPosture | null = null;

  if (!allPassed) {
    if (!g4 || hardConflict) {
      downgraded_to  = 'HIGH_RISK';
      forced_posture = 'REDUCE_RISK';
    } else if (!hasStructure || setupScore < 30) {
      downgraded_to  = 'WAIT';
      forced_posture = 'NO_TRADE';
    } else {
      downgraded_to  = 'DEVELOPING';
      forced_posture = 'NEUTRAL';
    }
  }

  return { passed: allPassed, results, failures, downgraded_to, forced_posture };
}

// ─── Confidence scorer ────────────────────────────────────────────────────────

export function scoreConfidence(
  input: ThesisEvidenceInput,
  gates: GateEvaluation,          // FIX: gate results now inform readiness
): ConfidenceBreakdown {
  const { smc, regime, zones, setups, risk } = input;

  // Structure confidence
  const structureConf = (() => {
    if (!smc) return 0;
    const base     = smc.biasStrength === 'major' ? 80 : 50;
    const hasBOS   = smc.lastBOS   ? 15 : 0;
    const hasCHOCH = smc.lastCHOCH ? 10 : 0;
    return Math.min(100, base + hasBOS + hasCHOCH);
  })();

  // Regime confidence
  const regimeConf = Math.min(100, regime?.confidence ?? 0);

  // Liquidity confidence
  const liquidityConf = (() => {
    if (!zones?.length) return 20;
    const highSig = zones.filter(z => z.sig === 'High').length;
    return Math.min(100, 30 + highSig * 18);
  })();

  // Setup confidence
  const topSetup  = setups[0] ?? null;
  const setupConf = topSetup?.confidence ?? 0;

  // Risk confidence
  const riskConf = (() => {
    if (!risk) return 10;
    const rrScore = risk.expectedR !== null
      ? Math.min(100, (risk.expectedR / 3) * 100) : 0;
    return Math.round((rrScore + risk.stopQuality + risk.targetQuality) / 3);
  })();

  // Alignment confidence
  const alignConf = (() => {
    let score = 50;
    const smcDir = smc?.bias === 'bullish' ? 'long' : smc?.bias === 'bearish' ? 'short' : null;
    if (!risk?.trapConflict)   score += 15;
    if (!risk?.regimeConflict) score += 20;
    if (topSetup && smcDir && topSetup.dir === smcDir) score += 15;
    return Math.min(100, score);
  })();

  // Weighted composite
  const overall = Math.round(
    structureConf * 0.25 +
    regimeConf    * 0.20 +
    liquidityConf * 0.10 +
    setupConf     * 0.25 +
    riskConf      * 0.10 +
    alignConf     * 0.10
  );

  // FIX: readiness_score is penalized for EVERY gate failure, not just traps
  const gateFailCount   = gates.results.filter(r => !r.passed).length;
  const gatePenalty     = Math.max(0, 1 - gateFailCount * 0.15);   // −15% per failed gate
  const readiness       = Math.round(overall * gatePenalty);

  return {
    structure_confidence:  structureConf,
    regime_confidence:     regimeConf,
    liquidity_confidence:  liquidityConf,
    setup_confidence:      setupConf,
    risk_confidence:       riskConf,
    alignment_confidence:  alignConf,
    overall_confidence:    overall,
    readiness_score:       readiness,
  };
}
